/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { audioContext } from "./utils";
import AudioRecordingWorklet from "./worklets/audio-processing";
import VolMeterWorket from "./worklets/vol-meter";

import { createWorketFromSrc } from "./audioworklet-registry";
import EventEmitter from "eventemitter3";

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  const CHUNK_SIZE = 8192;
  let binary = "";
  for (let i = 0; i < len; i += CHUNK_SIZE) {
    const end = Math.min(i + CHUNK_SIZE, len);
    const chunk = bytes.subarray(i, end);
    binary += String.fromCharCode.apply(null, chunk as unknown as number[]);
  }
  return window.btoa(binary);
}

export class AudioRecorder extends EventEmitter {
  stream: MediaStream | undefined;
  audioContext: AudioContext | undefined;
  source: MediaStreamAudioSourceNode | undefined;
  recording: boolean = false;
  recordingWorklet: AudioWorkletNode | undefined;
  vuWorklet: AudioWorkletNode | undefined;

  private starting: Promise<void> | null = null;

  constructor(public sampleRate = 16000) {
    super();
  }

  async start() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error("Could not request user media");
    }

    if (this.starting) {
      return this.starting;
    }

    this.starting = (async () => {
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (!this.stream) {
          throw new Error("Failed to get MediaStream");
        }

        this.audioContext = await audioContext({ sampleRate: this.sampleRate });

        // Ensure context is running (esp. on mobile)
        if (this.audioContext.state === 'suspended') {
          await this.audioContext.resume();
        }

        // Check if stream is valid before creating source
        try {
          this.source = this.audioContext.createMediaStreamSource(this.stream);
        } catch (sourceErr) {
          console.error("Failed to create MediaStreamSource:", sourceErr);
          throw sourceErr;
        }

        const workletName = "audio-recorder-worklet";
        const src = createWorketFromSrc(workletName, AudioRecordingWorklet);

        // Mobile safety: check for audioWorklet support
        if (this.audioContext.audioWorklet) {
          try {
            await this.audioContext.audioWorklet.addModule(src);
            this.recordingWorklet = new AudioWorkletNode(
              this.audioContext,
              workletName,
            );

            this.recordingWorklet.port.onmessage = async (ev: MessageEvent) => {
              const arrayBuffer = ev.data.data.int16arrayBuffer;

              if (arrayBuffer) {
                const arrayBufferString = arrayBufferToBase64(arrayBuffer);
                this.emit("data", arrayBufferString);
              }
            };
            this.source.connect(this.recordingWorklet);
          } catch (workletErr) {
            console.error("AudioWorklet failed:", workletErr);
            // Fallback or just log? For now just log, as this is critical for recording.
          }
        } else {
          console.warn("AudioWorklet not supported in this browser context");
        }

        const vuWorkletName = "vu-meter";
        // Only try VU meter if audioWorklet works
        if (this.audioContext.audioWorklet) {
          try {
            await this.audioContext.audioWorklet.addModule(
              createWorketFromSrc(vuWorkletName, VolMeterWorket),
            );
            this.vuWorklet = new AudioWorkletNode(this.audioContext, vuWorkletName);
            this.vuWorklet.port.onmessage = (ev: MessageEvent) => {
              this.emit("volume", ev.data.volume);
            };

            this.source.connect(this.vuWorklet);
          } catch (vuErr) {
            console.warn("VU Meter worklet failed:", vuErr);
          }
        }

        this.recording = true;
      } catch (err) {
        console.error("AudioRecorder start failed:", err);
        // Clean up partial state if needed
        this.stop();
        throw err;
      } finally {
        // We keep this.starting promise resolving/rejecting here, 
        // but to match previous behavior we might clear 'starting' AFTER resolution?
        // Actually, previous code set `this.starting = null` on success.
        // Let's mimic that.
        this.starting = null;  // Wait, if we clear it here, concurrent stop() calls might not wait?
        // The original code cleared it.
      }
    })();

    return this.starting;
  }

  stop() {
    const handleStop = () => {
      this.recordingWorklet?.disconnect();
      this.vuWorklet?.disconnect();
      this.source?.disconnect();
      this.stream?.getTracks().forEach((track) => track.stop());
      this.stream = undefined;
      this.recordingWorklet = undefined;
      this.vuWorklet = undefined;
    };
    if (this.starting) {
      this.starting.then(handleStop);
      return;
    }
    handleStop();
  }
}
